import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  MenuItemMapping, 
  MenuMappingsQuery,
  MappingFilter, 
  ImportResult, 
  BulkMappingSearchResult, 
  BulkMappingUpdateRequest, 
  PaginatedResponse
} from '../../types';
import { apiService } from '../../services/apiService';
import { showSuccessMessage, showErrorMessage } from './uiSlice';

interface MappingState {
  mappings: MenuItemMapping[];
  pagination: PaginatedResponse<MenuItemMapping>;
  selectedMapping: MenuItemMapping | null;
  loading: boolean;
  error: string | null;
  filter: MappingFilter;
  bulkOperationLoading: boolean;
  importResult: ImportResult | null;
  exportLoading: boolean;
  // New bulk operations state
  bulkSearchResults: BulkMappingSearchResult | null;
  bulkSearchLoading: boolean;
  bulkUpdateLoading: boolean;
}

const initialState: MappingState = {
  mappings: [],
  pagination: {
    data: [],
    totalCount: 0,
    pageSize: 25,
    currentPage: 1,
    totalPages: 0,
  },
  selectedMapping: null,
  loading: false,
  error: null,
  filter: {},
  bulkOperationLoading: false,
  importResult: null,
  exportLoading: false,
  // Initialize bulk operations state
  bulkSearchResults: null,
  bulkSearchLoading: false,
  bulkUpdateLoading: false,
};

// Async thunks
export const fetchMappingsForBranch = createAsyncThunk(
  'mappings/fetchForBranch',
  async (query: MenuMappingsQuery, { rejectWithValue, dispatch }) => {
    try {
      const mappings = await apiService.getMenuMappings(query);
      // Removed success notification for load operations to reduce noise
      return mappings;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch mappings';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

// Bulk operations async thunks
export const searchMappingsByCloudName = createAsyncThunk(
  'mappings/searchByCloudName',
  async (cloudItemName: string, { rejectWithValue, dispatch }) => {
    try {
      const results = await apiService.searchMappingsByCloudName(cloudItemName);
      dispatch(showSuccessMessage(`Found ${results.totalMappings} mappings across ${results.groupings.length} branches`));
      return results;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to search mappings by cloud item name';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkUpdateMappings = createAsyncThunk(
  'mappings/bulkUpdate',
  async (request: BulkMappingUpdateRequest, { rejectWithValue, dispatch }) => {
    try {
      const result = await apiService.bulkUpdateMappings(request);
      if (result.success) {
        dispatch(showSuccessMessage(`Successfully updated ${result.updatedCount} mappings`));
      } else {
        dispatch(showErrorMessage(`Bulk update completed with ${result.errors.length} errors`));
      }
      return result;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to perform bulk update';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const createMapping = createAsyncThunk(
  'mappings/create',
  async (mapping: Omit<MenuItemMapping, 'id' | 'lastUpdated' | 'createdAt'>, { rejectWithValue, dispatch }) => {
    try {
      const newMapping = await apiService.createMenuMapping(mapping);
      dispatch(showSuccessMessage(`Successfully created mapping for ${mapping.cloudMenuItemName}`));
      return newMapping;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create mapping';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateMapping = createAsyncThunk(
  'mappings/update',
  async (mapping: MenuItemMapping, { rejectWithValue, dispatch }) => {
    try {
      const updatedMapping = await apiService.updateMenuMapping(mapping.id, mapping);
      dispatch(showSuccessMessage(`Successfully updated mapping for ${mapping.cloudMenuItemName}`));
      return updatedMapping;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update mapping';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteMapping = createAsyncThunk(
  'mappings/delete',
  async ({ id, name }: { id: number; name: string }, { rejectWithValue, dispatch }) => {
    try {
      await apiService.deleteMenuMapping(id);
      dispatch(showSuccessMessage(`Successfully deleted mapping for ${name}`));
      return id;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete mapping';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const importMappingsFromFile = createAsyncThunk(
  'mappings/importFromFile',
  async ({ branchId, file }: { branchId?: string; file: File }, { rejectWithValue, dispatch }) => {
    try {
      const result = await apiService.importMappingsFromFile(branchId, file);
      if (result.success) {
        dispatch(showSuccessMessage(`Successfully imported ${result.importedCount} mappings and updated ${result.updatedCount} existing mappings`));
      } else {
        dispatch(showErrorMessage(`Import completed with errors. Check the import results for details.`));
      }
      return result;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to import mappings from file';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const exportMappings = createAsyncThunk(
  'mappings/export',
  async ({ branchId, format }: { branchId: string; format: 'csv' | 'excel' }, { rejectWithValue, dispatch }) => {
    try {
      const data = await apiService.exportMappings(branchId, format);
      dispatch(showSuccessMessage(`Successfully exported mappings to ${format.toUpperCase()} format`));
      return { data, format };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to export mappings';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

const mappingSlice = createSlice({
  name: 'mappings',
  initialState,
  reducers: {
    setSelectedMapping: (state, action: PayloadAction<MenuItemMapping | null>) => {
      state.selectedMapping = action.payload;
    },
    setFilter: (state, action: PayloadAction<MappingFilter>) => {
      state.filter = action.payload;
    },
    clearFilter: (state) => {
      state.filter = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    clearImportResult: (state) => {
      state.importResult = null;
    },
    updateMappingInList: (state, action: PayloadAction<MenuItemMapping>) => {
      const index = state.mappings.findIndex(m => m.id === action.payload.id);
      if (index !== -1) {
        state.mappings[index] = action.payload;
      }
    },
    // New bulk operations actions
    clearBulkSearchResults: (state) => {
      state.bulkSearchResults = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch mappings for branch
      .addCase(fetchMappingsForBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMappingsForBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.pagination = action.payload;
        state.mappings = action.payload.data;
      })
      .addCase(fetchMappingsForBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Create mapping
      .addCase(createMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMapping.fulfilled, (state, action) => {
        state.loading = false;
        state.mappings.push(action.payload);
      })
      .addCase(createMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update mapping
      .addCase(updateMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMapping.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.mappings.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.mappings[index] = action.payload;
        }
        if (state.selectedMapping?.id === action.payload.id) {
          state.selectedMapping = action.payload;
        }
      })
      .addCase(updateMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete mapping
      .addCase(deleteMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMapping.fulfilled, (state, action) => {
        state.loading = false;
        state.mappings = state.mappings.filter(m => m.id !== action.payload);
        if (state.selectedMapping?.id === action.payload) {
          state.selectedMapping = null;
        }
      })
      .addCase(deleteMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Import from file
      .addCase(importMappingsFromFile.pending, (state) => {
        state.bulkOperationLoading = true;
        state.error = null;
        state.importResult = null;
      })
      .addCase(importMappingsFromFile.fulfilled, (state, action) => {
        state.bulkOperationLoading = false;
        state.importResult = action.payload;
        // Refresh mappings if successful
        if (action.payload.success) {
          // Don't replace mappings here - let the component refetch them
        }
      })
      .addCase(importMappingsFromFile.rejected, (state, action) => {
        state.bulkOperationLoading = false;
        state.error = action.payload as string;
      })
      
      // Export generic
      .addCase(exportMappings.pending, (state) => {
        state.exportLoading = true;
        state.error = null;
      })
      .addCase(exportMappings.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportMappings.rejected, (state, action) => {
        state.exportLoading = false;
        state.error = action.payload as string;
      })
      
      // Bulk search by cloud name
      .addCase(searchMappingsByCloudName.pending, (state) => {
        state.bulkSearchLoading = true;
        state.error = null;
        state.bulkSearchResults = null;
      })
      .addCase(searchMappingsByCloudName.fulfilled, (state, action) => {
        state.bulkSearchLoading = false;
        state.bulkSearchResults = action.payload;
      })
      .addCase(searchMappingsByCloudName.rejected, (state, action) => {
        state.bulkSearchLoading = false;
        state.error = action.payload as string;
      })
      
      // Bulk update
      .addCase(bulkUpdateMappings.pending, (state) => {
        state.bulkUpdateLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateMappings.fulfilled, (state, action) => {
        state.bulkUpdateLoading = false;
        // If successful, clear bulk search results to force refresh
        if (action.payload.success) {
          state.bulkSearchResults = null;
        }
      })
      .addCase(bulkUpdateMappings.rejected, (state, action) => {
        state.bulkUpdateLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  setSelectedMapping, 
  setFilter, 
  clearFilter, 
  clearError, 
  clearImportResult,
  updateMappingInList,
  clearBulkSearchResults 
} = mappingSlice.actions;

export default mappingSlice.reducer;
